import { createSelector } from '@reduxjs/toolkit';
import { filter, get, indexOf } from 'lodash';
import { RootState } from 'src/root/store';

import type {
  CommunicationType,
  Country,
  Grade,
  MobilePhoneCarrier,
  NameSuffix,
  PhoneCountryCode,
  RelationshipType,
  State,
  SwimmingClassification,
  TitlePrefix,
} from '@appTypes/esb';
import type { MeritBadge } from '@appTypes/esb/advancements/meritBadges';
import { allowedPGRelationships, emptyArr } from '@shared/constants';
import { dictionarySel } from '@shared/duck/selectors/general.selectors';

import { dictionaryIds, moduleName } from '../constants';
import reducers from './reducers';

export const moduleSel = (state: RootState): ReturnType<typeof reducers> =>
  get(state, moduleName);

const dataSel = (state: RootState) => moduleSel(state).data;
export const dictionaryLoadedSel = (state: RootState, id: string) =>
  Boolean(dataSel(state)[id]);

export const relationshipTypesSel = (state: RootState) =>
  (dictionarySel(
    state,
    dictionaryIds.RELATIONSHIP_TYPES,
  ) as RelationshipType[]) || (emptyArr as unknown as RelationshipType[]);

const filterRelationshipTypes = (relationshipTypes: RelationshipType[]) =>
  filter(
    relationshipTypes,
    type => indexOf(allowedPGRelationships, type.name) !== -1,
  );

export const relationshipTypesFilteredSel = createSelector(
  relationshipTypesSel,
  unfilteredRel => filterRelationshipTypes(unfilteredRel),
);

export const meritBadgesSel = (state: RootState) =>
  (dictionarySel(state, dictionaryIds.MERIT_BADGES) as MeritBadge[]) ||
  (emptyArr as unknown as MeritBadge[]);

export const countriesSel = (state: RootState) =>
  (dictionarySel(state, dictionaryIds.COUNTRIES) as Country[]) || emptyArr;

export const statesSel = (state: RootState) =>
  (dictionarySel(state, dictionaryIds.STATES) as State[]) || emptyArr;

export const prefixesSel = (state: RootState) =>
  (dictionarySel(state, dictionaryIds.PREFIXES) as TitlePrefix[]) || emptyArr;

export const suffixesSel = (state: RootState) =>
  (dictionarySel(state, dictionaryIds.SUFFIXES) as NameSuffix[]) || emptyArr;

export const gradesSel = (state: RootState) =>
  (dictionarySel(state, dictionaryIds.GRADES) as Grade[]) || emptyArr;

export const phoneCarriersSel = (state: RootState) =>
  (dictionarySel(
    state,
    dictionaryIds.PHONE_CARRIERS,
  ) as MobilePhoneCarrier[]) || emptyArr;

export const phoneCountriesSel = (state: RootState) =>
  (dictionarySel(state, dictionaryIds.PHONE_COUNTRIES) as PhoneCountryCode[]) ||
  emptyArr;

export const comunicationTypesSel = (state: RootState) =>
  (dictionarySel(
    state,
    dictionaryIds.COMUNICATION_TYPES,
  ) as CommunicationType[]) || emptyArr;

export const swimmingClassificationsSel = (state: RootState) =>
  (dictionarySel(
    state,
    dictionaryIds.SWIMMIN_CLASSIFICATION,
  ) as SwimmingClassification[]) || emptyArr;

export const statesLoadedSel = (state: RootState) =>
  dictionaryLoadedSel(state, dictionaryIds.STATES);

export const phoneCountriesLoadedSel = (state: RootState) =>
  dictionaryLoadedSel(state, dictionaryIds.PHONE_COUNTRIES);
